import { BrowserRouter, useRoutes } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { DemoAuthProvider } from "@/contexts/DemoAuthContext";
import { IS_DEMO_MODE } from "@/config/config";
import { DemoGate } from "@/components/demo/DemoGate";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ThemeProvider } from 'next-themes'
import { PWAStatus } from '@/components/ui/PWAStatus'
import generateRoutes from "@/Routes";
import { useMemo } from "react";

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 1000 * 60 * 5, // 5 minutes
            gcTime: 1000 * 60 * 10, // 10 minutes
        },
    },
})

// The public demo has no accounts: every visitor gets the same read-only session.
const SessionProvider = IS_DEMO_MODE ? DemoAuthProvider : AuthProvider

// Router component that uses the dynamic routes
function AppRoutes() {
    const { user, userProfile, loading } = useAuth();

    const routes = useMemo(
        () => generateRoutes({ userProfile, loading, user }),
        // Only regenerate routes when auth state meaningfully changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [loading, (user as any)?.id ?? null, (userProfile as any)?.id ?? null]
    );

    const element = useRoutes(routes);
    return element;
}

// Main App component
function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
                <BrowserRouter>
                    <SessionProvider>
                        {IS_DEMO_MODE ? <DemoGate><AppRoutes /></DemoGate> : <AppRoutes />}
                        <PWAStatus />
                    </SessionProvider>
                </BrowserRouter>
            </ThemeProvider>
        </QueryClientProvider>
    );
}

export default App 